import {
  ConfirmationModal,
  Form,
  FormLabel,
  FormMultiLineTextInput,
  FormTextInput,
  SubTitle,
  TargetGroupDropdownSelector,
  ToggleRadioButtons,
} from 'ck-components';
import {
  PostPushNotification,
  QK,
  ScheduledNotification,
  ScheduledNotificationFormatted,
  useNotificationsCreate,
  useNotificationsEdit,
} from 'ck-queries';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  dateFormatInputCanAccept,
  getSavedPushNotificationValues,
  localStorageTyped,
  useTypedNavigate,
} from 'ck-utils';
import { useEffect, useMemo, useState } from 'react';

import { TargetAudienceCount } from 'src/components/TargetAudienceCount';
import { TargetDivisionDropdownSelector } from 'src/components/Form/TargetDivisionDropdownSelector/TargetDivisionDropdownSelector';
import dayjs from 'dayjs';
import { defaultPushNotificationValues } from './defaults';
import { useQueryTargetGroupsAudienceCount } from 'src/queries/targetGroups/useQueryTargetGroupsAudience';

interface Props {
  isEdit?: boolean;
  prefill?: ScheduledNotification | ScheduledNotificationFormatted;
  isCopy?: boolean;
}

/**
 * The form used to create push notifications.
 */

export function CreatePushNotificationForm({ prefill, isCopy, isEdit }: Props) {
  const { navigate } = useTypedNavigate();
  const M_CreateNotification = useNotificationsCreate();
  const M_EditNotification = useNotificationsEdit(prefill?.id || '');
  const form = useForm<PostPushNotification>({
    mode: 'onChange',
    defaultValues: getSavedPushNotificationValues(),
  });

  useEffect(() => {
    if (prefill) {
      // If prefill is provided, set the form values
      form.reset(prefill as PostPushNotification);
    }
  }, [prefill, form]);

  const division = form.watch('targetGroup.divisionName');
  const targetGroup = form.watch('targetGroup.group');

  const Q_TargetGroupsAudienceCount =
    useQueryTargetGroupsAudienceCount(division);

  const { watch, reset } = form;

  // Refetch audience count when division changes
  useEffect(() => {
    Q_TargetGroupsAudienceCount.refetch({
      queryKey: [QK.targetGroupsAudienceCount, division],
    });
  }, [division]);

  // Update Count based on selected target group
  const count = useMemo(() => {
    const data = Q_TargetGroupsAudienceCount.data;
    if (!data) return 0;

    const newCount =
      data.find(({ group }) => group === targetGroup)?.count ?? 0;
    form.setValue('targetGroup.count', newCount);
    return newCount;
  }, [Q_TargetGroupsAudienceCount.data, targetGroup, division]);

  const scheduleTime = watch('scheduleTime');
  type ScheduleSelection = 'Schemalägg' | 'Skicka nu';
  const scheduleSelectionOptions: Array<{
    label: string;
    value: ScheduleSelection;
  }> = [
    { label: 'Schemalägg', value: 'Schemalägg' },
    { label: 'Skicka nu', value: 'Skicka nu' },
  ];

  const [scheduleSelection, setScheduleSelection] =
    useState<ScheduleSelection>('Schemalägg');

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const onClear = () => {
    localStorageTyped.remove('FORM_CREATE_NOTIFICATION');
    reset(defaultPushNotificationValues);
  };

  const onSubmit: SubmitHandler<PostPushNotification> = async (data) => {
    if (scheduleSelection === 'Skicka nu') {
      data.scheduleTime = null;
    }
    if (isEdit) {
      M_EditNotification.mutateAsync(data).then(() => {
        onClear();
        navigate('/push-notifications/archive');
      });
    } else {
      M_CreateNotification.mutateAsync(data).then(() => {
        onClear();
        navigate('/push-notifications/archive');
      });
    }
  };

  const onSaveDraft = () => {
    form.setValue('isDraft', true);
    form.handleSubmit(onSubmit)();
  };

  return (
    <div className='max-w-7xl'>
      <Form
        onSubmit={() => setIsConfirmationOpen(true)}
        buttonType='button'
        submitButtonLabel={isEdit ? 'Spara' : 'Gå vidare'}
        submitButtonStyle='w-full sm:w-[200px]'
        form={form}
        formLocalStorageKey={!isCopy ? 'FORM_CREATE_NOTIFICATION' : undefined}
        onSaveDraft={onSaveDraft}
        onCleanForm={onClear}
      >
        <ConfirmationModal
          isOpen={isConfirmationOpen}
          setIsOpen={setIsConfirmationOpen}
          onConfirmation={() => {
            form.setValue('isDraft', false);
            form.handleSubmit(onSubmit)();
          }}
          modalTitle={
            scheduleSelection === 'Schemalägg'
              ? 'Schemalägg notis'
              : 'Skicka notis'
          }
          confirmationLabel={scheduleSelection}
        >
          <p className='max-w-md text-lg'>
            {scheduleSelection === 'Skicka nu' &&
              `Är du säker på att du vill skicka en notis till ${count} st?`}
            {scheduleSelection === 'Schemalägg' &&
              !isEdit &&
              `Vill du schemalägga notisen till ${
                scheduleTime && dayjs(scheduleTime).format('D MMMM kl. HH:mm')
              }?`}
            {isEdit &&
              `Detta kommer skriva över den tidigare schemalagda notisen. Vill du spara ändringarna?`}
          </p>
        </ConfirmationModal>
        <div className='flex flex-1 flex-col gap-6 md:flex-row md:gap-14'>
          <div className='flex-1'>
            <SubTitle label='Svenska' />
            <FormTextInput
              label={'RUBRIK'}
              parentForm={form}
              maxLength={40}
              name='titleSwedish'
            />
            <FormTextInput
              description={'Visas i pushnotisen'}
              label={'KORT MEDDELANDE'}
              parentForm={form}
              maxLength={100}
              name='notificationMessageSwedish'
            />
            <FormMultiLineTextInput
              label={'LÅNGT MEDDELANDE'}
              description={'Visas i appens inkorgsvy'}
              parentForm={form}
              type='textfield'
              maxLength={1000}
              name='inboxMessageSwedish'
            />
          </div>
          <div className='flex-1'>
            <SubTitle label='English' />
            <FormTextInput
              label={'TITLE'}
              parentForm={form}
              maxLength={40}
              name='titleEnglish'
            />
            <FormTextInput
              label={'SHORT MESSAGE'}
              description={'Displayed in the push notification'}
              parentForm={form}
              maxLength={100}
              name='notificationMessageEnglish'
            />
            <FormMultiLineTextInput
              label={'LONG MESSAGE'}
              description={`Displayed in the app's inbox`}
              parentForm={form}
              maxLength={1000}
              name='inboxMessageEnglish'
            />
          </div>
        </div>
        <hr />
        <div className='flex flex-col gap-6 md:flex-row md:gap-14'>
          <div className='flex-1'>
            <TargetGroupDropdownSelector name='targetGroup.group' form={form} />
          </div>
          {/* Invisible filler to get column width correct */}
          <div className='hidden flex-1 md:block' />
        </div>
        {/* Uncomment when we want to specify division
        <div className='flex flex-row  gap-14'>
          <div className='mt-[-30px] flex-1'>
            <TargetDivisionDropdownSelector
              name='targetGroup.divisionName'
              form={form}
            />
          </div>
          {/* Invisible filler to get column width correct 
          <div className='invisible flex-1'></div>
        </div> 
        */}
        <div className='mt-[-40px] flex flex-row gap-14'>
          <TargetAudienceCount count={count} />
          <div className='hidden flex-1 md:block'></div>
        </div>
        <div className='flex flex-col gap-6 md:flex-row md:gap-14'>
          <div className='flex-1'>
            <TargetDivisionDropdownSelector
              label='Avsändare / Sender'
              name='createdByDivisionName'
              form={form}
            />
          </div>
          {/* Invisible filler to get column width correct */}
          <div className='hidden flex-1 md:block' />
        </div>

        <FormLabel label={'TID FÖR UTSKICK'}>
          <>
            <ToggleRadioButtons
              selectedValue={scheduleSelection}
              selectOptions={scheduleSelectionOptions}
              disabled={Boolean(isEdit)}
              onChange={(e) =>
                setScheduleSelection(e.target.value as ScheduleSelection)
              }
            />
            {scheduleSelection === 'Schemalägg' && (
              <div className='mt-5 w-60'>
                <FormTextInput
                  parentForm={form}
                  maxLength={100}
                  minDate={dayjs().format(dateFormatInputCanAccept)}
                  name='scheduleTime'
                  type='datetime-local'
                  options={{
                    validate: (value) =>
                      dayjs(value).isAfter(dayjs()) ||
                      'Datumet måste vara i framtiden',
                  }}
                />
              </div>
            )}
          </>
        </FormLabel>
      </Form>
    </div>
  );
}
